"use client";

import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Image from "next/image";

gsap.registerPlugin(ScrollTrigger);

interface NumberedSectionProps {
  number: string;
  title: string;
  content: string;
  largeImage?: string;
  smallImages?: string[];
  id?: string;
  /** Rendered in place of `largeImage` — lets a section swap flat media for a live component. */
  media?: React.ReactNode;
  /** Second paragraph, placed below the media. */
  contentAfter?: string;
  /** Additional paragraph, placed below the media. */
  contentAfterExtra?: string;
  /** Follow-up paragraph, placed below `contentAfterExtra`. */
  contentAfterDetailed?: string;
  /** A single grid-sized image, placed below the post-media copy. */
  afterMediaImage?: string;
  /** Interactive replacement for the supporting image. */
  afterMedia?: React.ReactNode;
  /** Copy placed after the post-media grid image. */
  contentAfterImage?: string;
}

const NumberedSection: React.FC<NumberedSectionProps> = ({
  number,
  title,
  content,
  largeImage,
  smallImages = [],
  id,
  media,
  contentAfter,
  contentAfterExtra,
  contentAfterDetailed,
  afterMediaImage,
  afterMedia,
  contentAfterImage,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const largeMediaIsVideo = Boolean(
    largeImage && /\.(mp4|webm|ogg)(?:\?.*)?$/i.test(largeImage),
  );

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Text animation
      gsap.fromTo(
        ".section-text",
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 85%",
          },
        }
      );

      // Image animations
      gsap.fromTo(
        ".section-image",
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".section-images",
            start: "top 85%",
          },
        }
      );

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id={id} className="py-24 border-t border-[#e8e8e8]">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Text Content */}
          <div className="grid lg:grid-cols-2 gap-16 items-start mb-16">
            {/* Left Column - Number (Full Column) */}
            <div className="flex justify-start">
              <div className="section-text flex items-center justify-center mb-4 font-pp-neuebit">
                <span className="text-[#e8e8e8] font-bold text-6xl">
                  {number}
                </span>
              </div>
            </div>

            {/* Right Column - Title and Content (Full Column) */}
            <div>
              <h2 className="section-text text-3xl md:text-4xl font-bold text-[#111111] mb-6 font-pp-acma">
                {title}
              </h2>
              <div className="section-text prose prose-lg max-w-none">
                <p className="text-xl text-[#6b6b6b] leading-relaxed font-neue-regrade-variable">
                  {content}
                </p>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="section-images space-y-6 pt-12">
            {media && <div className="section-image">{media}</div>}

            {/* Large Image */}
            {!media && largeImage && (
              <div className="section-image aspect-[16/9] bg-[#f4f4f5] overflow-hidden rounded-lg">
                {largeMediaIsVideo ? (
                  <video
                    src={largeImage}
                    aria-label={title}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    disablePictureInPicture
                    className="pointer-events-none h-full w-full object-cover"
                  />
                ) : largeImage.startsWith('/') || largeImage.startsWith('http') ? (
                  <Image
                    src={largeImage}
                    alt={title}
                    width={1200}
                    height={675}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#f4f4f5]">
                    <span className="text-gray-400 text-lg font-medium">
                      {largeImage}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Small Images Grid */}
            {smallImages.length > 0 && (
              <div className="grid md:grid-cols-2 gap-6">
                {smallImages.map((image, index) => (
                  <div
                    key={index}
                    className="section-image aspect-[4/3] bg-[#f4f4f5] overflow-hidden rounded-lg"
                  >
                    {image.startsWith('/') || image.startsWith('http') ? (
                      <Image
                        src={image}
                        alt={`${title} - Image ${index + 1}`}
                        width={600}
                        height={450}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#f4f4f5]">
                        <span className="text-gray-400">{image}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {contentAfter && (
              <div className="section-image grid lg:grid-cols-2 gap-16 pt-6">
                <div className="hidden lg:block" aria-hidden="true" />
                <p className="text-xl text-[#6b6b6b] leading-relaxed font-neue-regrade-variable">
                  {contentAfter}
                </p>
              </div>
            )}

            {contentAfterExtra && (
              <div className="section-image grid lg:grid-cols-2 gap-16 pt-6">
                <div className="hidden lg:block" aria-hidden="true" />
                <p className="text-xl text-[#6b6b6b] leading-relaxed font-neue-regrade-variable">
                  {contentAfterExtra}
                </p>
              </div>
            )}

            {contentAfterDetailed && (
              <div className="section-image grid lg:grid-cols-2 gap-16 pt-6">
                <div className="hidden lg:block" aria-hidden="true" />
                <p className="text-xl text-[#6b6b6b] leading-relaxed font-neue-regrade-variable">
                  {contentAfterDetailed}
                </p>
              </div>
            )}

            {afterMedia && <div className="section-image pt-6">{afterMedia}</div>}

            {!afterMedia && afterMediaImage && (
              <div className="pt-6">
                <div className="section-image aspect-[16/9] bg-[#f4f4f5] overflow-hidden rounded-lg">
                  {afterMediaImage.startsWith('/') || afterMediaImage.startsWith('http') ? (
                    <Image
                      src={afterMediaImage}
                      alt={`${title} — supporting image`}
                      width={1800}
                      height={1012}
                      sizes="(min-width: 1280px) 1280px, calc(100vw - 32px)"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[#f4f4f5]">
                      <span className="text-gray-400">{afterMediaImage}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {contentAfterImage && (
              <div className="section-image grid lg:grid-cols-2 gap-16 pt-6">
                <div className="hidden lg:block" aria-hidden="true" />
                <p className="text-xl text-[#6b6b6b] leading-relaxed font-neue-regrade-variable">
                  {contentAfterImage}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NumberedSection;
